/**
 * Firework rockets as projectiles. Plain rockets only fly and vanish; rockets carrying stars explode for
 * 5 + 2 per extra star (within 5 blocks, scaled by sqrt((5 - d) / 5), blocked by walls) and burst in the
 * star colours. Flight duration 1..3 and star colours live in `Stack.fw` = [duration, ...colours].
 * DOM-free: the game wires `FwHost` to mobs, the player, particles and sound.
 */
import { Glider } from '../player/elytra';
import { I } from './itemIds';
import type { Stack } from './types';

export const DYE_COLOURS: ReadonlyArray<readonly [number, number]> = [
  [I.INK_SAC, 0x1d1d21], [I.LAPIS_LAZULI, 0x3c44aa], [I.BONE_MEAL, 0xf9fffe],
];
export const dyeColour = (id: number): number | undefined => DYE_COLOURS.find((d) => d[0] === id)?.[1];

/** Flight duration 1..3 (0 / absent reads as 1). */
export const rocketDuration = (s: Stack): number => Math.min(3, Math.max(1, s.fw?.[0] ?? 1));
export const rocketStars = (s: Stack): number[] => (s.fw ? s.fw.slice(1) : []);
export const explosionDamage = (stars: number): number => (stars > 0 ? 5 + 2 * (stars - 1) : 0);

export interface FwTarget {
  x: number; y: number; z: number; height: number;
  /** Identity of whoever this is (a player id); a rocket never hits its own shooter in flight. */
  id?: unknown;
  hurt(damage: number, fromX: number, fromZ: number, owner?: unknown): void;
}
export interface FwHost {
  targets(): readonly FwTarget[];
  solid(x: number, y: number, z: number): boolean;
  /** True when a wall lies between the two points. */
  blocked(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): boolean;
  trail(x: number, y: number, z: number): void;
  burst(x: number, y: number, z: number, colours: readonly number[]): void;
  sound(id: string, x: number, y: number, z: number): void;
}

/** Damage `host`'s targets around a burst; returns how many were hurt. */
export function fireworkExplode(host: FwHost, x: number, y: number, z: number, stars: readonly number[], owner?: unknown): number {
  if (stars.length === 0) return 0;
  host.burst(x, y, z, stars);
  // vanilla picks the sound by the star's shape / twinkle flag; stars here carry only a colour, so many stars read as a large ball
  host.sound(stars.length >= 3 ? 'entity.firework_rocket.large_blast' : 'entity.firework_rocket.blast', x, y, z);
  if (stars.length >= 2) host.sound('entity.firework_rocket.twinkle', x, y, z);
  const dmg = explosionDamage(stars.length);
  let n = 0;
  for (const t of host.targets()) {
    const cy = t.y + t.height / 2;
    const d = Math.hypot(t.x - x, cy - y, t.z - z);
    if (d > 5 || host.blocked(x, y, z, t.x, cy, t.z)) continue;
    t.hurt(dmg * Math.sqrt((5 - d) / 5), x, z, owner);
    n++;
  }
  return n;
}

export interface Rocket {
  x: number; y: number; z: number; vx: number; vy: number; vz: number;
  /** Rises with vanilla's self-acceleration (hand launch) instead of flying straight. */
  accel: boolean;
  age: number; life: number; stars: number[]; shooter: FwTarget | null;
  /** Who launched it (a player id on the server); credited with kills and skipped by the flight's entity test. */
  owner?: unknown;
}

export class RocketField {
  readonly rockets: Rocket[] = [];
  constructor(readonly host: FwHost) {}

  launch(x: number, y: number, z: number, v: [number, number, number], s: Stack, accel: boolean, rnd: () => number = Math.random): Rocket {
    const r: Rocket = {
      x, y, z, vx: v[0], vy: v[1], vz: v[2], accel, age: 0, life: Glider.rocketLife(rocketDuration(s), rnd),
      stars: rocketStars(s), shooter: null,
    };
    this.rockets.push(r);
    this.host.sound('entity.firework_rocket.launch', x, y, z);
    return r;
  }

  /** One game tick: fly (in steps of at most half a block, so 1.6 b/t cannot skip a wall), trail, explode on expiry / entity / block. */
  tick(): void {
    for (let i = this.rockets.length - 1; i >= 0; i--) {
      const r = this.rockets[i];
      if (r.accel) { r.vy = r.vy * 1.15 + 0.04; }
      const steps = r.accel ? 1 : Math.max(1, Math.ceil(Math.hypot(r.vx, r.vy, r.vz) / 0.5));
      r.age++;
      let hit = false;
      for (let k = 0; k < steps && !hit; k++) {
        const px = r.x, py = r.y, pz = r.z;
        r.x += r.vx / steps; r.y += r.vy / steps; r.z += r.vz / steps;
        if (r.accel) continue;
        // a wall: burst at the last free point so the blast has a line of sight out of it
        if (this.host.solid(Math.floor(r.x), Math.floor(r.y), Math.floor(r.z))) { r.x = px; r.y = py; r.z = pz; hit = true; break; }
        for (const t of this.host.targets()) {
          if (t === r.shooter || (r.owner !== undefined && t.id === r.owner)) continue;
          if (Math.hypot(t.x - r.x, t.z - r.z) < 0.6 && r.y >= t.y - 0.3 && r.y <= t.y + t.height + 0.3) { hit = true; break; }
        }
      }
      this.host.trail(r.x, r.y, r.z);
      if (hit || r.age >= r.life) { this.rockets.splice(i, 1); fireworkExplode(this.host, r.x, r.y, r.z, r.stars, r.owner); }
    }
  }
}
